<template>
  <!--
    The button is the root, so it is a direct child of the `v-btn-toggle` grouping the toolbar
    actions - the group styles and registers its immediate buttons only. The badge therefore sits
    on the icon rather than around the button.
  -->
  <v-btn icon :aria-label="$t('components.activity.ActivityInbox.title')" @click="click">
    <v-badge :value="hasUnseen" :content="`${unseenCount}`" overlap data-test-activity-inbox-badge>
      <v-icon>mdi-inbox</v-icon>
    </v-badge>
  </v-btn>
</template>

<script lang="ts">
import {
  combineFilters,
  forTargetUser,
  IWssActivityEventData,
  IWssEvent,
  IWssSubscriptions,
  WssEventNameEnum
} from "@/lib/wss";
import { ActivityLog } from "@/models/activity-log.entity";
import { ActivityInboxModule } from "@/store/modules/activity-inbox.store";
import { UserModule } from "@/store/modules/me-user.store";
import { PartnerModule } from "@/store/modules/partner";
import WssSubscriptionMixin from "@/mixins/WssSubscriptionMixin";
import { mixins } from "vue-class-component";
import { Component } from "vue-property-decorator";

/**
 * Opens the activity inbox and badges it while activities the user received arrive over the websocket.
 *
 * The badge is the only "unread" state there is: the backend keeps no read state for activities, so
 * opening the inbox clears it.
 */
@Component({})
export default class ActivityInboxButton extends mixins(WssSubscriptionMixin) {
  get userId(): string {
    return UserModule.userId;
  }

  get partnerId(): string {
    return PartnerModule.partner?.id ?? "";
  }

  get unseenCount(): number {
    return ActivityInboxModule.unseenCount;
  }

  get hasUnseen(): boolean {
    return ActivityInboxModule.hasUnseen;
  }

  /**
   * Every activity that can reference the user as a target, so the inbox stays in sync with what a
   * reload of the activity log would show.
   */
  get wssSubscriptions(): IWssSubscriptions {
    const activityEvents = [
      WssEventNameEnum.ANY_COMMENT_CREATE,
      WssEventNameEnum.ANY_ASSIGNEE_ADD,
      WssEventNameEnum.ANY_ASSIGNEE_DELETE
    ];

    return {
      disabled: !this.userId,
      subscriptions: activityEvents.map((event) => ({
        event,
        filter: combineFilters<IWssActivityEventData>([
          (activityEvent) => activityEvent.partnerId === this.partnerId,
          forTargetUser(() => this.userId)
        ]),
        handler: (activityEvent: IWssEvent<IWssActivityEventData>) => this.onActivity(activityEvent)
      }))
    };
  }

  /**
   * The event carries the id of the activity only, so it is fetched before it can be listed.
   */
  async onActivity(event: IWssEvent<IWssActivityEventData>): Promise<void> {
    const activityId = event.data?.activityId;
    if (!activityId || !event.partnerId) {
      return;
    }

    try {
      const activity = await new ActivityLog({ partnerId: event.partnerId, id: activityId }).fetch();

      // An activity the user caused themselves - assigning themselves to a ticket, for instance - is
      // listed but does not badge: they do not need to be notified of what they just did.
      const authorId = activity.userId ?? activity.user?.id;

      await ActivityInboxModule.receive({ activity, badge: authorId !== this.userId });
    } catch (error) {
      this.$log.error("[ActivityInboxButton] Could not fetch the activity of the event", error);
    }
  }

  click() {
    ActivityInboxModule.markAllSeen();
    this.$emit("click");
  }
}
</script>
